import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Play, FileText, X, Download, Check, Mail, Calculator, Send } from 'lucide-react';
import { formatINR, formatDateTime } from '../../lib/format';
import { apiGet, apiPost, apiPut, apiDownload } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

const downloadPayslipPDF = (id) => {
  window.open(`/api/hr/payroll/${id}/pdf`, '_blank');
};

function formatYM(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default function Payroll() {
  const today = new Date();
  const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const [month, setMonth] = useState(formatYM(lastMonth));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [payslip, setPayslip] = useState(null);
  const [versionConfirm, setVersionConfirm] = useState(null);  // legacy + new confirmations
  const [showRecompute, setShowRecompute] = useState(false);

  const fetchPayroll = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiGet('/hr/payroll', { month });
      setData(d);
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { fetchPayroll(); }, [fetchPayroll]);

  // §4: runPayroll handles multiple 409 confirmation keys
  const runPayroll = async ({
    confirmVersionChange = false,
    confirmRerun = false,
    confirmIncompleteMonth = false,
    overrideReason = null,
  } = {}) => {
    setRunning(true);
    try {
      let url = `/hr/payroll/run?month=${month}`;
      if (confirmVersionChange) url += '&confirm_version_change=true';
      if (confirmRerun) url += '&confirm_rerun=true';
      if (confirmIncompleteMonth) url += '&confirm_incomplete_month=true';
      if (overrideReason) url += `&override_reason=${encodeURIComponent(overrideReason)}`;
      const d = await apiPost(url, {});
      toast.success(`Payroll calculated for ${d.summary.employees} employees`);
      if (d.summary.arrears_reapplied > 0) toast.info(`${d.summary.arrears_reapplied} arrears re-applied from previous run`);
      if (d.summary.arrears_created) toast.info(`${d.summary.arrears_created} employee(s) queued for arrears next month`);
      if (d.summary.arrears_exceptions_flagged) toast.warning(`${d.summary.arrears_exceptions_flagged} overpaid-on-rerun exception(s) need manual review`);
      fetchPayroll();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409 && detail?.requires_confirmation) {
        setVersionConfirm(detail);
      } else if (status === 403) {
        toast.error("You don't have permission to run payroll. This requires HR Manager access.");
      } else {
        toast.error(e?.response?.data?.detail || 'Payroll run failed');
      }
    } finally {
      setRunning(false);
    }
  };

  // §8: Release payroll month to employees
  const releasePayroll = async () => {
    if (!window.confirm(`Release ${month} payslips to all employees? They will be able to view and download.`)) return;
    setReleasing(true);
    try {
      const d = await apiPost(`/hr/payroll/${month}/release`, {});
      if (d.already_released) {
        toast.info(`${month} was already released.`);
      } else {
        toast.success(`${d.released} payslips released to employees.`);
      }
      fetchPayroll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Release failed');
    } finally {
      setReleasing(false);
    }
  };

  const markPaid = async (id) => {
    try {
      await apiPut(`/hr/payroll/${id}/mark-paid`, {});
      toast.success('Marked as paid');
      fetchPayroll();
    } catch {
      toast.error('Failed');
    }
  };

  const [emailingPayslips, setEmailingPayslips] = useState(false);
  const emailAllPayslips = async () => {
    if (!window.confirm(`Email payslips for ${month} to every employee with an email address?`)) return;
    setEmailingPayslips(true);
    try {
      const d = await apiPost('/hr/payroll/email-payslips', { month });
      toast.success(`Sent ${d.sent} payslips · ${d.skipped.length} skipped · ${d.failed.length} failed`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to email payslips');
    } finally {
      setEmailingPayslips(false);
    }
  };

  const summary = data?.summary || {};
  const rows = data?.rows || [];

  return (
    <div className="space-y-4" data-testid="payroll-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            data-testid="payroll-month"
            className="border border-slate-200 rounded-md px-3 py-2 text-sm"
          />
          <Button onClick={() => runPayroll()} disabled={running} className="bg-[#3b4872] hover:bg-[#2c3658]" data-testid="run-payroll-btn">
            <Play size={14} className="mr-1.5" /> {running ? 'Running…' : (rows.length > 0 ? 'Re-run' : 'Run Payroll')}
          </Button>
          {/* §8: Release payroll to employees */}
          {rows.length > 0 && (
            <Button onClick={releasePayroll} disabled={releasing} variant="outline" data-testid="release-payroll-btn"
              title="Release payslips so employees can view them">
              <Send size={14} className="mr-1.5" /> {releasing ? 'Releasing…' : (rows[0]?.released ? 'Released' : 'Release to Employees')}
            </Button>
          )}
          {rows.length > 0 && (
            <Button onClick={emailAllPayslips} disabled={emailingPayslips} variant="outline" data-testid="email-payslips-btn">
              <Mail size={14} className="mr-1.5" /> {emailingPayslips ? 'Sending…' : 'Email All Payslips'}
            </Button>
          )}
          <Button onClick={() => setShowRecompute(true)} variant="outline" data-testid="recompute-months-btn">
            <Calculator size={14} className="mr-1.5" /> Recompute months
          </Button>
          {rows.length > 0 && (
            <Button onClick={() => apiDownload(`/hr/payroll/${month}/salary-sheet/export`, `salary_sheet_${month}.xlsx`)}
              variant="outline" data-testid="export-salary-sheet-btn">
              <Download size={14} className="mr-1.5" /> Export salary sheet
            </Button>
          )}
        </div>
        {rows.length > 0 && (
          <div className="text-xs text-slate-500">
            Last run: {rows[0]?.run_at ? formatDateTime(rows[0].run_at) : '—'} by {rows[0]?.run_by || '—'}
          </div>
        )}
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        <KPI label="Employees" value={summary.employees || 0} />
        <KPI label="Gross" value={formatINR(summary.total_gross)} />
        <KPI label="PF (Emp)" value={formatINR(summary.total_pf_employee || summary.total_pf)} dim />
        <KPI label="PF (Employer)" value={formatINR(summary.total_pf_employer)} dim />
        <KPI label="ESI (Emp)" value={formatINR(summary.total_esi_employee || summary.total_esi)} dim />
        <KPI label="Gratuity Prov." value={formatINR(summary.total_gratuity_provision)} dim />
        <KPI label="Net Pay" value={formatINR(summary.total_net)} accent />
        <KPI label="Employer Cost" value={formatINR(summary.total_employer_cost)} accent />
      </div>

      {(summary.esic_covered_count !== undefined) && (
        <div className="bg-slate-50 border border-slate-200 rounded-sm px-4 py-2 text-xs text-slate-700 flex items-center gap-4">
          <span><strong>{summary.esic_covered_count}</strong> employees under <strong>ESIC</strong> (Basic ≤ ₹21k)</span>
          <span className="text-slate-300">·</span>
          <span><strong>{summary.gpa_covered_count}</strong> under <strong>GPA + Medical Insurance</strong> (Basic &gt; ₹21k)</span>
        </div>
      )}

      {/* Round 3 #2: Integrity failures banner */}
      {data?.summary?.integrity_failures && data.summary.integrity_failures.length > 0 && (
        <div className="bg-rose-50 border border-rose-300 rounded-sm px-4 py-2.5 text-xs text-rose-800" data-testid="integrity-failures-banner">
          <div className="font-bold mb-1">⚠ {data.summary.integrity_failures.length} payslip integrity failure(s) — run completed but needs review:</div>
          {data.summary.integrity_failures.slice(0, 5).map((f, i) => (
            <div key={i}>{f.emp_code} {f.name}: <em>{f.rule}</em> expected {f.expected} got {f.actual}</div>
          ))}
          {data.summary.integrity_failures.length > 5 && <div>…and {data.summary.integrity_failures.length - 5} more</div>}
        </div>
      )}
      {data?.summary?.integrity_ok === true && rows.length > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-sm px-4 py-2 text-xs text-emerald-700" data-testid="integrity-ok-banner">
          All {rows.length} payslips passed integrity check.
        </div>
      )}
        <div className="bg-amber-50 border border-amber-300 rounded-sm px-4 py-2.5 text-xs text-amber-800 flex items-center gap-2" data-testid="structure-over-allocated-banner">
          <strong>⚠ Salary structure is over-allocated</strong> — components sum to more than 100% of gross for one or more employees.
          This month still ran, but correct the structure in Salary Structure Master before the next cycle.
        </div>
      )}
      {(summary.arrears_created > 0 || summary.arrears_applied_total > 0 || summary.arrears_exceptions_flagged > 0) && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-sm px-4 py-2.5 text-xs text-indigo-800 flex items-center gap-4" data-testid="arrears-summary-banner">
          {summary.arrears_applied_total > 0 && <span>Arrears paid out this month: <strong>{formatINR(summary.arrears_applied_total)}</strong></span>}
          {summary.arrears_created > 0 && <span>{summary.arrears_created} employee(s) queued for arrears next month</span>}
          {summary.arrears_exceptions_flagged > 0 && <span className="text-rose-700">{summary.arrears_exceptions_flagged} exception(s) need manual review — see Arrears &amp; Exceptions below</span>}
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto overflow-y-auto max-h-[70vh]">
            <table className="w-full text-sm sticky-col-header">
              <thead className="bg-slate-50 border-b border-slate-200 text-[11px] font-display uppercase tracking-[0.14em] text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2.5">Emp</th>
                  <th className="text-left px-3 py-2.5">Name</th>
                  <th className="text-center px-3 py-2.5">Days</th>
                  <th className="text-right px-3 py-2.5">Basic</th>
                  <th className="text-right px-3 py-2.5">HRA</th>
                  <th className="text-right px-3 py-2.5">Allow</th>
                  <th className="text-right px-3 py-2.5 bg-amber-50/20" title="One-time earnings + arrears applied this month">Arr/1-time</th>
                  <th className="text-right px-3 py-2.5 bg-emerald-50/40">Gross</th>
                  <th className="text-right px-3 py-2.5">PF</th>
                  <th className="text-right px-3 py-2.5">ESI</th>
                  <th className="text-right px-3 py-2.5">PT</th>
                  <th className="text-right px-3 py-2.5">TDS</th>
                  <th className="text-right px-3 py-2.5 bg-amber-50/40">Adv. EMI</th>
                  <th className="text-right px-3 py-2.5 bg-indigo-50/40">Net Pay</th>
                  <th className="text-center px-3 py-2.5">Status</th>
                  <th className="text-right px-3 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={15} className="text-center py-8 text-slate-500">Loading…</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={15} className="text-center py-12 text-slate-500">
                    No payroll for {month}. Click <strong>Run Payroll</strong> to calculate.
                  </td></tr>
                ) : rows.map(r => (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-indigo-50/20" data-testid={`payroll-row-${r.emp_code}`}>
                    <td className="px-3 py-2 font-mono text-xs">{r.emp_code}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{r.employee_name}</div>
                      <div className="text-[11px] text-slate-500">{r.designation}</div>
                    </td>
                    <td className="px-3 py-2 text-center text-xs">
                      {r.payable_days}/{r.days_in_month}
                      {r.lop_days > 0 && <div className="text-red-500">LOP {r.lop_days}</div>}
                    </td>
                    <td className="px-3 py-2 text-right">{formatINR(r.basic)}</td>
                    <td className="px-3 py-2 text-right">{formatINR(r.hra)}</td>
                    <td className="px-3 py-2 text-right">{formatINR(r.allowances)}</td>
                    <td className="px-3 py-2 text-right text-amber-700 bg-amber-50/20" data-testid={`payroll-arrears-${r.emp_code}`}>
                      {(r.one_time_earnings_total || r.arrears_applied_total)
                        ? formatINR((r.one_time_earnings_total || 0) + (r.arrears_applied_total || 0))
                        : '—'}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold bg-emerald-50/40">{formatINR(r.gross)}</td>
                    <td className="px-3 py-2 text-right text-slate-500">{formatINR(r.pf)}</td>
                    <td className="px-3 py-2 text-right text-slate-500">{formatINR(r.esi)}</td>
                    <td className="px-3 py-2 text-right text-slate-500">{formatINR(r.pt)}</td>
                    <td className="px-3 py-2 text-right text-slate-500">{formatINR(r.tds)}</td>
                    <td className={`px-3 py-2 text-right bg-amber-50/40 ${r.advance_recovery_total ? 'text-amber-800 font-semibold' : 'text-slate-300'}`} data-testid={`payroll-adv-emi-${r.emp_code}`}>
                      {r.advance_recovery_total ? formatINR(r.advance_recovery_total) : '—'}
                      {r.advance_recovery_short && <div className="text-[10px] text-red-500 font-normal">short</div>}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-[#3b4872] bg-indigo-50/40">{formatINR(r.net_pay)}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-sm ${
                        r.status === 'Paid' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                      }`}>{r.status}</span>
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button onClick={() => setPayslip(r)} className="text-slate-500 hover:text-[#3b4872] mr-2" title="View Payslip" data-testid={`view-payslip-${r.emp_code}`}>
                        <FileText size={14} />
                      </button>
                      <button onClick={() => downloadPayslipPDF(r.id)} className="text-slate-500 hover:text-[#3b4872] mr-2" title="Download PDF" data-testid={`download-pdf-${r.emp_code}`}>
                        <Download size={14} />
                      </button>
                      {r.status !== 'Paid' && (
                        <button onClick={() => markPaid(r.id)} className="text-emerald-600 hover:bg-emerald-50 p-1 rounded" title="Mark Paid" data-testid={`pay-${r.emp_code}`}>
                          <Check size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {payslip && <PayslipModal payslip={payslip} onClose={() => setPayslip(null)} />}
      {versionConfirm && (
        <RunConfirmModal
          detail={versionConfirm}
          month={month}
          onCancel={() => setVersionConfirm(null)}
          onConfirm={(opts) => { setVersionConfirm(null); runPayroll(opts); }}
        />
      )}
      <ArrearsExceptionsPanel />
      <PFEnrolmentPanel />
      {showRecompute && <RecomputeModal onClose={() => setShowRecompute(false)} />}
    </div>
  );
}

// Unified payroll run confirmation modal — handles version-change, rerun, incomplete-month
function RunConfirmModal({ detail, month, onCancel, onConfirm }) {
  const [overrideReason, setOverrideReason] = React.useState('');
  const key = detail.confirmation_key || 'confirm_version_change';

  const isIncomplete = key === 'confirm_incomplete_month';
  const isRerun = key === 'confirm_rerun';
  const isVersion = key === 'confirm_version_change';

  const handleConfirm = () => {
    if (isIncomplete && !overrideReason.trim()) {
      toast.error('You must enter a reason to override the incomplete-month guard.'); return;
    }
    onConfirm({
      confirmVersionChange: isVersion,
      confirmRerun: isRerun || isVersion,
      confirmIncompleteMonth: isIncomplete,
      overrideReason: overrideReason.trim() || null,
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-md w-full max-w-md p-5" data-testid="run-confirm-modal">
        <h3 className="font-display font-bold text-base text-amber-800 mb-2">
          {isIncomplete ? 'Month not yet complete' : isRerun ? 'Replace existing run?' : 'Salary structure changed'}
        </h3>
        <p className="text-sm text-slate-600 mb-3">{detail.reason}</p>
        {(detail.run_at || detail.employees_affected) && (
          <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-xs text-slate-700 space-y-1 mb-4">
            {detail.run_at && <div>Previous run: <strong>{detail.run_at?.slice(0,16)?.replace('T',' ')}</strong> by {detail.run_by}</div>}
            {detail.employees_affected && <div>Employees affected: <strong>{detail.employees_affected}</strong></div>}
            {detail.old_total_net && <div>Previous net: <strong>{formatINR(detail.old_total_net)}</strong></div>}
            {detail.days_remaining && <div>Days remaining in month: <strong>{detail.days_remaining}</strong></div>}
            {detail.unmarked_employee_count != null && <div>Employees with no attendance: <strong>{detail.unmarked_employee_count}</strong> of {detail.total_employees}</div>}
          </div>
        )}
        {/* Round 3 #2: preview_changes — top 20 net-pay changes computed without saving */}
        {isRerun && detail.preview_changes && detail.preview_changes.length > 0 && (
          <div className="mb-4">
            <div className="text-xs font-semibold text-slate-600 mb-1">Top net-pay changes (preview, not saved):</div>
            <div className="border border-slate-200 rounded-sm overflow-x-auto max-h-40 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1.5">Code</th>
                    <th className="text-left px-2 py-1.5">Name</th>
                    <th className="text-right px-2 py-1.5">Old Gross</th>
                    <th className="text-right px-2 py-1.5">New Gross</th>
                    <th className="text-right px-2 py-1.5">Old Net</th>
                    <th className="text-right px-2 py-1.5">New Net</th>
                    <th className="text-right px-2 py-1.5">Δ Net</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {detail.preview_changes.map((c, i) => (
                    <tr key={i} className={i % 2 === 0 ? '' : 'bg-slate-50/50'}>
                      <td className="px-2 py-1 font-mono">{c.emp_code}</td>
                      <td className="px-2 py-1">{c.name}</td>
                      <td className="px-2 py-1 text-right font-mono">{formatINR(c.old_gross)}</td>
                      <td className="px-2 py-1 text-right font-mono">{formatINR(c.new_gross)}</td>
                      <td className="px-2 py-1 text-right font-mono">{formatINR(c.old_net)}</td>
                      <td className="px-2 py-1 text-right font-mono">{formatINR(c.new_net)}</td>
                      <td className={`px-2 py-1 text-right font-mono font-semibold ${c.delta > 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{c.delta > 0 ? '+' : ''}{formatINR(c.delta)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {isRerun && detail.preview_changes && detail.preview_changes.length === 0 && (
          <div className="mb-4 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-2">
            Preview: no net-pay changes detected — attendance and gross are identical to the previous run.
          </div>
        )}
        {isIncomplete && (
          <div className="mb-4">
            <label className="text-xs font-semibold text-slate-600 block mb-1">Override reason (required — super_admin only)</label>
            <input value={overrideReason} onChange={e => setOverrideReason(e.target.value)}
              placeholder="e.g. Client required payroll before month-end" data-testid="override-reason-input"
              className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm" />
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} className="px-4 py-2 border border-slate-300 rounded-sm text-sm" data-testid="run-confirm-cancel">Cancel</button>
          <button onClick={handleConfirm} className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-sm text-sm font-semibold" data-testid="run-confirm-proceed">
            {isIncomplete ? 'Override and run' : isRerun ? 'Confirm and run' : 'Re-run with new structure'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RecomputeModal({ onClose }) {
  const [fromMonth, setFromMonth] = useState('2026-04');
  const [toMonth, setToMonth] = useState('2026-08');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const runPreview = async () => {
    setLoading(true);
    try {
      const d = await apiGet('/hr/payroll/recompute-preview', { from_month: fromMonth, to_month: toMonth });
      setPreview(d);
    } catch (e) {
      toast.error(e?.response?.status === 403 ? 'Only super_admin / HR Manager can recompute.' : 'Preview failed');
    } finally {
      setLoading(false);
    }
  };

  const createArrears = async () => {
    if (!window.confirm('Create Pending-approval arrears entries for these differences? Nothing is paid until approved.')) return;
    setCreating(true);
    try {
      const d = await apiPost(`/hr/payroll/recompute-months?from_month=${fromMonth}&to_month=${toMonth}`, {});
      toast.success(`${d.created} arrears queued for approval · ${d.skipped_existing} already existed`);
      runPreview();
    } catch (e) {
      toast.error(e?.response?.status === 403 ? 'Only super_admin / HR Manager can recompute.' : 'Failed to create arrears');
    } finally {
      setCreating(false);
    }
  };

  const exportXlsx = () => apiDownload(
    `/hr/payroll/recompute-months/export?from_month=${fromMonth}&to_month=${toMonth}`,
    `wo_ho_recompute_${fromMonth}_to_${toMonth}.xlsx`,
  );

  const t = preview?.totals;
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-md w-full max-w-4xl my-6" data-testid="recompute-modal">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h3 className="font-display font-bold text-base">Recompute months — Weekly-off / Holiday arrears</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="recompute-close"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-xs text-slate-500 leading-snug">
            Recomputes each employee's pay with Weekly-off / Holiday counted as paid days, and compares against the
            old (WO/HO-dropped) calculation. Positive differences are queued as <strong>Pending-approval</strong> arrears —
            nothing is paid until an approver approves each entry, which then rides the next payroll month.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">From</label>
              <input type="month" value={fromMonth} onChange={e => setFromMonth(e.target.value)} className="border border-slate-200 rounded-md px-3 py-2 text-sm" data-testid="recompute-from" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">To</label>
              <input type="month" value={toMonth} onChange={e => setToMonth(e.target.value)} className="border border-slate-200 rounded-md px-3 py-2 text-sm" data-testid="recompute-to" />
            </div>
            <Button onClick={runPreview} disabled={loading} className="bg-[#3b4872] hover:bg-[#2c3658]" data-testid="recompute-preview-btn">
              {loading ? 'Computing…' : 'Preview'}
            </Button>
            {preview && (
              <>
                <Button onClick={exportXlsx} variant="outline" data-testid="recompute-export-btn"><Download size={14} className="mr-1.5" /> Export xlsx</Button>
                <Button onClick={createArrears} disabled={creating || (t?.row_count || 0) === 0} className="bg-emerald-600 hover:bg-emerald-700" data-testid="recompute-create-btn">
                  {creating ? 'Creating…' : 'Create arrears (pending approval)'}
                </Button>
              </>
            )}
          </div>

          {t && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm" data-testid="recompute-totals">
              <div className="bg-slate-50 border rounded-sm p-3"><div className="text-[10px] uppercase text-slate-500">Employees</div><div className="font-bold">{t.employees}</div></div>
              <div className="bg-slate-50 border rounded-sm p-3"><div className="text-[10px] uppercase text-slate-500">Rows</div><div className="font-bold">{t.row_count}</div></div>
              <div className="bg-emerald-50 border rounded-sm p-3"><div className="text-[10px] uppercase text-slate-500">Total Net Diff</div><div className="font-bold text-emerald-700">{formatINR(t.total_net_delta)}</div></div>
              <div className="bg-indigo-50 border rounded-sm p-3"><div className="text-[10px] uppercase text-slate-500">Already Created</div><div className="font-bold">{t.already_created}</div></div>
            </div>
          )}

          {preview && (
            <div className="overflow-auto max-h-[45vh] border rounded-sm">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0 text-[10px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-2 py-2">Emp</th>
                    <th className="text-left px-2 py-2">Name</th>
                    <th className="text-left px-2 py-2">Month</th>
                    <th className="text-center px-2 py-2">WO/HO</th>
                    <th className="text-center px-2 py-2">Old→New Days</th>
                    <th className="text-right px-2 py-2" title="PF on the arrear days only — past PF is never re-based (B5)">PF Diff</th>
                    <th className="text-right px-2 py-2">Net Diff</th>
                    <th className="text-left px-2 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.length === 0 ? (
                    <tr><td colSpan={8} className="text-center py-8 text-slate-500">No differences found for this range.</td></tr>
                  ) : preview.rows.map((r, i) => (
                    <tr key={`${r.employee_id}-${r.month}`} className="border-t border-slate-100" data-testid={`recompute-row-${i}`}>
                      <td className="px-2 py-1.5 font-mono">{r.emp_code}</td>
                      <td className="px-2 py-1.5">{r.employee_name}</td>
                      <td className="px-2 py-1.5">{r.month}</td>
                      <td className="px-2 py-1.5 text-center">{r.wo_ho_days}</td>
                      <td className="px-2 py-1.5 text-center">{r.old_payable_days} → <strong>{r.new_payable_days}</strong></td>
                      <td className="px-2 py-1.5 text-right font-mono text-slate-600" data-testid={`recompute-pf-delta-${i}`} title={`${r.pf_basis || ''} · arrear PF wages ${formatINR(r.arrear_pf_wages || 0)}`}>{formatINR(r.pf_delta || 0)}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-emerald-700">{formatINR(r.net_delta)}</td>
                      <td className="px-2 py-1.5">
                        {r.existing_arrears_status
                          ? <span className="text-[10px] bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded">{r.existing_arrears_status}</span>
                          : <span className="text-[10px] text-slate-400">not created</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ArrearsExceptionsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(null);
  const { user } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiGet('/hr/payroll/arrears');
      setRows(d || []);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const resolve = async (id, note) => {
    try {
      await apiPut(`/hr/payroll/arrears/${id}/resolve`, { note });
      toast.success('Exception resolved');
      load();
    } catch {
      toast.error('Failed to resolve');
    } finally {
      setResolving(null);
    }
  };

  const approve = async (id) => {
    try {
      await apiPost(`/hr/payroll/arrears/${id}/approve`, {});
      toast.success('Arrears approved — will ride next payroll');
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      if (e?.response?.status === 403) toast.error(msg || 'Cannot approve: permission denied or self-approval');
      else toast.error('Failed to approve');
    }
  };

  const callerName = user?.name || '';
  const callerEmail = user?.email || '';

  // §3: hide Approve when this is the caller's own row (created_by == caller, or own employee_id)
  const canApprove = (r) => {
    if (!r) return false;
    const cb = r.created_by || '';
    if (cb && (cb === callerName || cb === callerEmail)) return false;
    return true;
  };

  const active = rows.filter(r => r.status === 'pending' || r.status === 'exception' || r.status === 'pending_approval');
  if (loading || active.length === 0) return null;

  const statusMeta = {
    exception: { cls: 'bg-rose-50 text-rose-700', label: 'Overpaid — needs review' },
    pending: { cls: 'bg-amber-50 text-amber-700', label: 'Pending next payroll' },
    pending_approval: { cls: 'bg-sky-50 text-sky-700', label: 'Pending approval' },
  };

  return (
    <Card data-testid="arrears-exceptions-panel">
      <CardContent className="p-4">
        <h3 className="font-display font-bold text-sm mb-3">Arrears &amp; Exceptions</h3>
        <table className="w-full text-xs">
          <thead className="text-slate-500 uppercase tracking-wider text-[10px]">
            <tr>
              <th className="text-left py-1.5">Employee</th>
              <th className="text-left py-1.5">Source Month</th>
              <th className="text-right py-1.5">Net Delta</th>
              <th className="text-left py-1.5">Status</th>
              <th className="text-right py-1.5">Action</th>
            </tr>
          </thead>
          <tbody>
            {active.map(r => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`arrears-row-${r.id}`}>
                <td className="py-1.5">{r.employee_name}</td>
                <td className="py-1.5">{r.source_month}</td>
                <td className={`py-1.5 text-right font-mono ${r.delta_amount < 0 ? 'text-rose-600' : 'text-emerald-700'}`}>{formatINR(r.delta_amount)}</td>
                <td className="py-1.5">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-sm ${(statusMeta[r.status] || {}).cls || 'bg-slate-100 text-slate-600'}`}>
                    {(statusMeta[r.status] || {}).label || r.status}
                  </span>
                </td>
                <td className="py-1.5 text-right">
                  {r.status === 'pending_approval' && canApprove(r) && (
                    <button onClick={() => approve(r.id)} className="text-emerald-700 underline text-[11px] font-semibold" data-testid={`approve-arrears-${r.id}`}>Approve</button>
                  )}
                  {r.status === 'pending_approval' && !canApprove(r) && (
                    <span className="text-slate-400 text-[10px]" data-testid={`approve-blocked-${r.id}`}>Cannot approve own</span>
                  )}
                  {r.status === 'exception' && (
                    resolving === r.id ? (
                      <ResolveNoteInput onSubmit={(note) => resolve(r.id, note)} onCancel={() => setResolving(null)} />
                    ) : (
                      <button onClick={() => setResolving(r.id)} className="text-[#3b4872] underline text-[11px]" data-testid={`resolve-arrears-${r.id}`}>Resolve</button>
                    )
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function ResolveNoteInput({ onSubmit, onCancel }) {
  const [note, setNote] = useState('');
  return (
    <div className="flex items-center gap-1.5 justify-end">
      <input
        autoFocus
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Resolution note…"
        className="border border-slate-300 rounded-sm px-2 py-1 text-xs w-32"
        data-testid="resolve-arrears-note-input"
      />
      <button onClick={() => note.trim() && onSubmit(note.trim())} className="text-emerald-700 text-[11px] font-semibold">Save</button>
      <button onClick={onCancel} className="text-slate-400 text-[11px]">✕</button>
    </div>
  );
}

function KPI({ label, value, accent, dim }) {
  return (
    <Card className={accent ? 'border-l-4 border-l-[#3b4872]' : ''}>
      <CardContent className="p-3">
        <div className="text-[10px] font-display uppercase tracking-[0.16em] text-slate-500">{label}</div>
        <div className={`text-base font-bold mt-1 ${accent ? 'text-[#3b4872]' : dim ? 'text-slate-600' : 'text-slate-800'}`}>
          {value}
        </div>
      </CardContent>
    </Card>
  );
}

function PayslipModal({ payslip: summary, onClose }) {
  // Round 2 #1: fetch the full detail (which has `rows` from payslip_rows())
  // so we render the same view as the PDF — no employer PF lines, arrears visible.
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(true);
  const p = detail || summary;   // fall back to summary while loading

  useEffect(() => {
    setLoadingDetail(true);
    apiGet(`/hr/payroll/payslip/${summary.id}`)
      .then(d => setDetail(d))
      .catch(() => {})
      .finally(() => setLoadingDetail(false));
  }, [summary.id]);

  const rows = detail?.rows || null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-md w-full max-w-2xl my-6" onClick={e => e.stopPropagation()} data-testid="payslip-modal">
        <div className="flex items-center justify-between border-b px-5 py-3 print:hidden">
          <h3 className="font-display font-bold text-lg">Payslip · {p.month}</h3>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => downloadPayslipPDF(p.id)} className="bg-[#3b4872] hover:bg-[#2c3658]" data-testid="payslip-download-pdf">
              <Download size={13} className="mr-1" /> Download PDF
            </Button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
          </div>
        </div>
        <div className="p-6">
          <div className="text-center border-b pb-3 mb-4">
            <div className="font-display font-extrabold text-xl tracking-tight">ELLORA EPC PVT LTD</div>
            <div className="text-xs text-slate-500 mt-0.5">Payslip for the month of {p.month}</div>
          </div>

          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm mb-4">
            <Row k="Employee" v={`${p.emp_code || ''} · ${p.employee_name}`.trim().replace(/^\·\s*/, '').replace(/\s*·\s*$/, '')} />
            <Row k="Designation" v={p.designation} />
            <Row k="Department" v={p.department} />
            <Row k="UAN" v={p.uan} />
            <Row k="PAN" v={p.pan} />
            <Row k="Bank A/C" v={p.bank_account ? `${p.bank_account.slice(-4).padStart(p.bank_account.length, 'X')} (${p.ifsc})` : '—'} />
            <Row k="Days Payable" v={`${p.payable_days} of ${p.days_in_month}`} />
            <Row k="LOP" v={p.lop_days || 0} />
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-sm px-4 py-2.5 mb-4 grid grid-cols-2 md:grid-cols-6 gap-3 text-[11px]" data-testid="payslip-day-buckets">
            <DayBucket label="Present" v={p.present_days} />
            <DayBucket label="Weekly off / Holiday" v={p.wo_ho_days} />
            <DayBucket label="Half-Day" v={p.half_days} sub="× 0.5" />
            <DayBucket label="Paid Leave" v={p.paid_leave_days} />
            <DayBucket label="LOP" v={p.lop_days} accent={(p.lop_days || 0) > 0 ? 'rose' : null} />
            <DayBucket label="Effective" v={p.effective_days ?? p.payable_days} accent="indigo" />
          </div>

          {loadingDetail && (
            <div className="text-center text-slate-400 text-sm py-4">Loading payslip detail…</div>
          )}

          {rows ? (
            /* Render from payslip_rows() — SSOT, no employer-side lines */
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-[11px] font-display uppercase tracking-[0.16em] text-emerald-700 mb-1 flex items-center gap-2">
                  Earnings
                  {(p.lop_days || 0) > 0 && p.gross_full && (
                    <span className="text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">Prorated</span>
                  )}
                </div>
                <table className="w-full text-sm" data-testid="payslip-earnings-table">
                  <tbody>
                    {rows.earnings.map(([label, amount], i) => (
                      <PayRow key={i} k={label} v={amount} />
                    ))}
                    <PayRow k="Gross" v={p.gross} bold testId="payslip-gross" />
                    {rows.loan_payouts.map(([label, amount], i) => (
                      <PayRow key={`lp-${i}`} k={label} v={amount} />
                    ))}
                  </tbody>
                </table>
                {(p.lop_days || 0) > 0 && p.gross_full && p.gross_full !== p.gross && (
                  <div className="text-[10px] text-slate-500 mt-1.5 leading-snug">
                    Full gross (₹{Math.round(p.gross_full).toLocaleString('en-IN')}) prorated by{' '}
                    <strong>{p.effective_days ?? p.payable_days}/{p.days_in_month}</strong> days.
                  </div>
                )}
              </div>
              <div>
                <div className="text-[11px] font-display uppercase tracking-[0.16em] text-red-700 mb-1">Deductions</div>
                <table className="w-full text-sm" data-testid="payslip-deductions-table">
                  <tbody>
                    {rows.deductions.map(([label, amount], i) => (
                      <PayRow key={i} k={label} v={amount} />
                    ))}
                    <PayRow k="Total Deductions" v={rows.total_deductions} bold testId="payslip-total-deductions" />
                  </tbody>
                </table>
                <div className="text-[10px] text-slate-500 mt-2 leading-snug">
                  <div>{pfBracketNote(p)}</div>
                </div>
                {rows.notes.map((n, i) => (
                  <div key={i} className="text-[10px] text-amber-700 mt-1.5 leading-snug">{n}</div>
                ))}
              </div>
            </div>
          ) : !loadingDetail && (
            /* Fallback for legacy payslips without rows (pre-Round 1) */
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-[11px] font-display uppercase tracking-[0.16em] text-emerald-700 mb-1">Earnings</div>
                <table className="w-full text-sm">
                  <tbody>
                    {(Array.isArray(p.components_earned) && p.components_earned.length
                      ? p.components_earned
                      : [
                          { code: 'BASIC', name: 'Basic', amount: p.basic },
                          { code: 'HRA', name: 'HRA', amount: p.hra },
                          { code: 'ALLOW', name: 'Allowances', amount: p.allowances },
                        ]
                    ).map((c, i) => <PayRow key={c.code || i} k={c.name || c.code} v={c.amount} />)}
                    <PayRow k="Gross" v={p.gross} bold testId="payslip-gross" />
                  </tbody>
                </table>
              </div>
              <div>
                <div className="text-[11px] font-display uppercase tracking-[0.16em] text-red-700 mb-1">Deductions</div>
                <table className="w-full text-sm">
                  <tbody>
                    {(p.pf_inclusion_mode !== 'both_above') && (
                      <PayRow k={`${pfLabel(p)} — Employee`} v={p.pf_employee ?? p.pf} />
                    )}
                    <PayRow k={`ESIC${p.coverage_type === 'ESIC' ? ' (0.75%)' : ' — N/A'}`} v={p.esi_employee ?? p.esi} />
                    <PayRow k={ptLabel(p)} v={p.pt} />
                    <PayRow k="TDS (FY 26-27 New Regime)" v={p.tds} />
                    <PayRow k="Total Deductions" v={p.deductions} bold testId="payslip-total-deductions" />
                  </tbody>
                </table>
                <div className="text-[10px] text-slate-500 mt-2 leading-snug">{pfBracketNote(p)}</div>
              </div>
            </div>
          )}

          <div className="mt-5 bg-indigo-50 border-l-4 border-[#3b4872] px-4 py-3 flex items-center justify-between">
            <div className="text-sm font-display uppercase tracking-[0.18em] text-slate-700">Net Pay</div>
            <div className="text-2xl font-extrabold text-[#3b4872]">{formatINR(p.net_pay)}</div>
          </div>
          {(p.advance_outstanding_after || 0) > 0 && (
            <div className="mt-2 text-xs text-slate-600" data-testid="payslip-advance-outstanding">
              Salary advance outstanding after this month: <strong>{formatINR(p.advance_outstanding_after)}</strong>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex">
      <span className="text-slate-500 w-32">{k}</span>
      <span className="font-medium">{v || '—'}</span>
    </div>
  );
}

function PayRow({ k, v, bold, testId }) {
  return (
    <tr className={bold ? 'border-t border-slate-200' : ''} data-testid={testId}>
      <td className={`py-1 text-slate-600 ${bold ? 'font-bold' : ''}`}>{k}</td>
      <td className={`py-1 text-right ${bold ? 'font-bold' : ''}`} data-amount={v}>{formatINR(v)}</td>
    </tr>
  );
}

function DayBucket({ label, v, sub, accent }) {
  const colorMap = {
    rose: 'text-rose-700',
    indigo: 'text-indigo-700',
  };
  return (
    <div>
      <div className="text-[10px] font-display uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-base font-bold leading-tight ${accent ? colorMap[accent] : 'text-slate-800'}`}>
        {v ?? 0}{sub && <span className="text-[10px] text-slate-400 font-normal ml-1">{sub}</span>}
      </div>
    </div>
  );
}

function pfLabel(p) {
  const of = p.pf_wage_label || 'Basic';
  if (p.pf_opt_out) return 'PF (Opted out · Form 11)';
  if (p.pf_opt_out_cap) return `PF (12% of ${of} · No Cap)`;
  if (p.pf_employer_capped) {
    const last = [...(p.pf_periods || [])].reverse().find(x => x.pf_active);
    const ceiling = Number(last?.ceiling || 25000);
    return `PF (12% of ${of} · Capped @ ₹${(ceiling / 1000).toFixed(0)}k)`;
  }
  return `PF (12% of ${of})`;
}

function ptLabel(p) {
  // Backend charges ₹300 only in February (annual ₹2500 cap)
  const isFeb = typeof p.month === 'string' && p.month.endsWith('-02');
  return isFeb ? 'Professional Tax (Feb top-up)' : 'Professional Tax';
}

// Explains the PF inclusion rule applied on this payslip, driven by the
// employee's contract gross bracket. Oct 2026+: ≤₹15k / ₹15k-₹25k / >₹25k.
function pfBracketNote(p) {
  const mode = p.pf_inclusion_mode || 'both_within';
  if (mode === 'both_above')
    return 'Gross ≤ ₹15,000 — both Employee PF and Employer PF are employer-paid benefits above gross. Take-home reflects no PF deduction.';
  if (mode === 'employee_within')
    return 'Gross ₹15,001 – ₹25,000 — only Employee PF is netted from gross. Employer PF is an employer-paid benefit on top.';
  return 'Gross > ₹25,000 — both Employee PF and Employer PF are netted from gross (CTC-inclusive gross model).';
}


// PF Enrolment Panel — PF_Engine_WageCeiling_25000_26Sep2026 §D
function PFEnrolmentPanel() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [enrolling, setEnrolling] = React.useState(null);
  const [ecrMonth, setEcrMonth] = React.useState(new Date().toISOString().slice(0,7));
  const [ecrData, setEcrData] = React.useState(null);

  const load = async () => {
    setLoading(true);
    try { setData(await apiGet('/hr/payroll/pf-enrolment-due')); }
    catch { /* non-critical */ }
    finally { setLoading(false); }
  };
  React.useEffect(() => { load(); }, []);

  const enrol = async (empId, empName) => {
    if (!window.confirm(`Enrol ${empName} into PF from 2026-09-17 (Gazette notification date)?`)) return;
    setEnrolling(empId);
    try {
      await apiPost('/hr/payroll/pf-enrolment', { employee_id: empId, enrol_from: '2026-09-17' });
      toast.success(`${empName} enrolled in PF from 17-Sep-2026`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Enrolment failed'); }
    finally { setEnrolling(null); }
  };

  const loadEcr = async () => {
    try {
      const d = await apiGet(`/hr/statutory/pf-register/${ecrMonth}/ecr`);
      setEcrData(d);
    } catch (e) { toast.error(e?.response?.data?.detail || 'ECR generation failed'); }
  };

  const downloadEcr = () => {
    const API = process.env.REACT_APP_BACKEND_URL;
    window.open(`${API}/api/hr/statutory/pf-register/${ecrMonth}/ecr/download`, '_blank');
  };

  if (loading || !data) return null;
  const { must_enrol = [], has_uan_review = [], ceiling = 25000 } = data;
  if (must_enrol.length === 0 && has_uan_review.length === 0 && !ecrData) {
    return (
      <Card data-testid="pf-enrolment-panel">
        <CardContent className="p-4">
          <h3 className="font-display font-bold text-sm mb-2">PF Enrolment &amp; ECR Export</h3>
          <div className="flex items-center gap-3">
            <input type="month" value={ecrMonth} onChange={e => setEcrMonth(e.target.value)} className="border rounded px-2 py-1 text-sm" data-testid="ecr-month-picker" />
            <button onClick={loadEcr} className="px-3 py-1.5 bg-[#3b4872] text-white rounded-sm text-xs font-semibold" data-testid="ecr-preview-btn">Preview ECR</button>
            <button onClick={downloadEcr} className="px-3 py-1.5 border border-[#3b4872] text-[#3b4872] rounded-sm text-xs font-semibold" data-testid="ecr-download-btn">Download ECR 2.0</button>
          </div>
          {ecrData && (
            <div className="mt-3 text-xs text-slate-600">
              <p>Members with UAN: <strong>{ecrData.member_count}</strong> · Missing UAN: <strong>{ecrData.missing_uan?.length || 0}</strong></p>
              {ecrData.missing_uan?.length > 0 && (
                <p className="text-amber-700 mt-1">{ecrData.missing_uan.slice(0,5).map(m => m.name).join(', ')}{ecrData.missing_uan.length > 5 ? ` +${ecrData.missing_uan.length - 5} more` : ''} — UAN missing, excluded from ECR.</p>
              )}
              <EcrRowsPreview rows={ecrData.rows_preview} />
            </div>
          )}
          <p className="text-xs text-slate-400 mt-2">No employees pending PF enrolment (ceiling Rs.{Number(ceiling).toLocaleString('en-IN')}).</p>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card data-testid="pf-enrolment-panel">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold text-sm">PF Enrolment Due &amp; ECR Export</h3>
          <span className="text-xs text-slate-500">Ceiling effective 17-Sep-2026: Rs.{Number(ceiling).toLocaleString('en-IN')}</span>
        </div>
        {must_enrol.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-rose-700 mb-2" data-testid="pf-enrol-count">{must_enrol.length} employees must be enrolled from 17-Sep-2026 (PF wages below new ceiling)</p>
            <table className="w-full text-xs">
              <thead className="text-slate-500 uppercase tracking-wider text-[10px]">
                <tr><th className="text-left py-1">Employee</th><th className="text-right py-1">PF Wages</th><th className="text-right py-1">UAN</th><th className="text-right py-1">Action</th></tr>
              </thead>
              <tbody>
                {must_enrol.map(emp => (
                  <tr key={emp.id} className="border-t border-slate-100" data-testid={`enrol-row-${emp.emp_id}`}>
                    <td className="py-1.5">{emp.name}<span className="text-slate-400 ml-1">({emp.emp_id})</span></td>
                    <td className="py-1.5 text-right font-mono">Rs.{Number(emp.pf_wages || 0).toLocaleString('en-IN')}</td>
                    <td className="py-1.5 text-right">{emp.uan || <span className="text-slate-400">—</span>}</td>
                    <td className="py-1.5 text-right">
                      {emp.pf_member_from ? (
                        <span className="text-emerald-700 text-[10px]">Enrolled {emp.pf_member_from}</span>
                      ) : (
                        <button onClick={() => enrol(emp.id, emp.name)} disabled={enrolling === emp.id}
                          className="text-[#3b4872] underline text-[11px] font-semibold disabled:opacity-50" data-testid={`enrol-btn-${emp.emp_id}`}>
                          {enrolling === emp.id ? 'Enrolling…' : 'Enrol from 17-Sep'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {has_uan_review.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-amber-700 mb-1">Review: {has_uan_review.length} opted-out employees hold a UAN (existing PF member cannot be excluded)</p>
            <p className="text-xs text-slate-500">{has_uan_review.map(e => e.name).join(', ')}</p>
          </div>
        )}
        <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
          <input type="month" value={ecrMonth} onChange={e => setEcrMonth(e.target.value)} className="border rounded px-2 py-1 text-sm" data-testid="ecr-month-picker" />
          <button onClick={loadEcr} className="px-3 py-1.5 bg-[#3b4872] text-white rounded-sm text-xs font-semibold" data-testid="ecr-preview-btn">Preview ECR</button>
          <button onClick={downloadEcr} className="px-3 py-1.5 border border-[#3b4872] text-[#3b4872] rounded-sm text-xs font-semibold" data-testid="ecr-download-btn">Download ECR 2.0</button>
          {ecrData && <span className="text-xs text-slate-600">{ecrData.member_count} members · {ecrData.missing_uan?.length || 0} missing UAN</span>}
        </div>
        {ecrData && <EcrRowsPreview rows={ecrData.rows_preview} />}
      </CardContent>
    </Card>
  );
}

function EcrRowsPreview({ rows }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="mt-3 overflow-x-auto" data-testid="ecr-rows-preview">
      <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-1.5">EPF / EPS / EDLI / Admin breakdown (first {rows.length})</p>
      <table className="w-full text-[11px]">
        <thead className="text-slate-500 uppercase tracking-wider text-[9px]">
          <tr>
            <th className="text-left py-1">Employee</th><th className="text-right py-1">EPF Wages</th><th className="text-right py-1">EPS Wages</th>
            <th className="text-right py-1">EDLI Wages</th><th className="text-right py-1">EE</th><th className="text-right py-1">ER-EPF</th>
            <th className="text-right py-1">ER-EPS</th><th className="text-right py-1">EDLI</th><th className="text-right py-1">Admin</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-slate-100">
              <td className="py-1">{r.name} <span className="text-slate-400">({r.uan})</span></td>
              <td className="py-1 text-right font-mono">{r.epf_wages}</td>
              <td className="py-1 text-right font-mono">{r.eps_wages}</td>
              <td className="py-1 text-right font-mono">{r.edli_wages}</td>
              <td className="py-1 text-right font-mono">{r.ee}</td>
              <td className="py-1 text-right font-mono">{r.er_epf}</td>
              <td className="py-1 text-right font-mono">{r.er_eps}</td>
              <td className="py-1 text-right font-mono">{r.edli}</td>
              <td className="py-1 text-right font-mono">{r.admin}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
